import React, {
  Fragment,
  TouchEvent as ReactTouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildScore } from '@/atoms/GuildScore'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { PlayCircleOutlineIcon } from '@/atoms/Icons/PlayCircleOutlineIcon'
import { ClosedCaptionsBadge, ContentRatingBadge, HighDefinitionBadge } from '@/atoms/MetadataBadge'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { CaptionMD, CaptionSM, LabelMD } from '@/atoms/Text'
import { BoundedImage } from '@/molecules/BoundedImage'
import { WatchlistButton } from '@/molecules/WatchlistButton'
import { LinkViewModel } from '@/services/RenderService'
import { useUser } from '@/services/UserService'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface BannerCarouselDisplayProps {
  heroBanners: LinkViewModel[]
  className?: string
  currentBanner: LinkViewModel
  setCurrentBanner: React.Dispatch<React.SetStateAction<LinkViewModel>>
}

export const BannerCarouselDisplay: React.FC<BannerCarouselDisplayProps> = ({
  heroBanners,
  className,
  currentBanner,
  setCurrentBanner,
}) => {
  const { isLoggedIn } = useUser()
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const [currentSlide, setCurrentSlide] = useState(0)
  const minSwipeDistance = 200
  const contentRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslate('watch')

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === heroBanners.length - 1 ? 0 : prev + 1))
  }, [heroBanners.length])

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? heroBanners.length - 1 : prev - 1))
  }, [heroBanners.length])

  const onTouchStart = (event: ReactTouchEvent) => {
    setTouchEnd(0)
    const firstTouch = event.targetTouches[0]
    if (firstTouch) {
      setTouchStart(firstTouch.clientX)
    }
  }

  const onTouchMove = (event: ReactTouchEvent) => {
    const firstTouch = event.targetTouches[0]
    if (firstTouch) {
      setTouchEnd(firstTouch.clientX)
    }
  }

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd

    if (distance > minSwipeDistance) {
      nextSlide()
    } else if (distance < -minSwipeDistance) {
      prevSlide()
    }
  }, [touchStart, touchEnd, nextSlide, prevSlide])

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentSlide === (heroBanners?.length ?? 0) - 1) {
        setCurrentSlide(0)
      } else {
        setCurrentSlide(currentSlide + 1)
      }
    }, 6500)
    return () => clearInterval(interval)
  }, [currentSlide, heroBanners?.length])

  useEffect(() => {
    setCurrentBanner(heroBanners?.[currentSlide] || ({} as LinkViewModel))
  }, [currentSlide, heroBanners, setCurrentBanner])

  const logoPath = useMemo(() => {
    const banner = heroBanners[currentSlide]
    if (!banner) return ''

    return currentBanner?.logoUrl
  }, [currentBanner?.logoUrl, currentSlide, heroBanners])

  const ctaButtonProps = useMemo(() => {
    const banner = heroBanners[currentSlide]
    if (!banner) return { ctaText: '', icon: null, href: '/' }

    return {
      ctaText: t('startWatching', 'Start Watching'),
      icon: <PlayCircleOutlineIcon color={'core-gray-950'} />,
      href: currentBanner.linkUrl,
    }
  }, [currentBanner.linkUrl, currentSlide, heroBanners, t])

  return (
    <div
      className="group-watch-banner relative flex flex-col"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <Fragment key={currentSlide}>
        <Transition
          show
          appear
          enter="transition-opacity duration-1000"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-5500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={classNames([
              'flex flex-col gap-4 relative w-full lg:items-center min-h-[60vh] lg:flex-row lg:flex-wrap md:min-h-[45vh] lg:min-h-[50vh] xl:min-h-[53vh] 2xl:min-h-[630px] bg-transparent',
              className,
            ])}
            ref={contentRef}
          >
            <PaddedContainer fluid className="flex items-center justify-center">
              <div className="relative mt-[23vh] w-full pt-24 md:mt-6 lg:mt-10 lg:max-w-[600px] xl:max-w-[650px]">
                <BoundedImage
                  className="mb-4 flex min-h-[64px] items-center justify-center md:mb-2 md:justify-start"
                  alt={''}
                  path={logoPath ?? ''}
                  width={600}
                  height={104}
                  imageClassName="se:max-h-[64px]"
                />
                <div className="mb-2 lg:mb-4">
                  <CaptionMD
                    className="line-clamp-2 min-h-9 text-pretty text-center md:max-w-[420px] md:text-left lg:min-h-[18px] lg:max-w-[500px] xl:max-w-[550px]"
                    weight="medium"
                  >
                    {currentBanner?.description}
                  </CaptionMD>
                </div>
                <div className="mb-2 flex flex-row flex-wrap items-center justify-center gap-1 md:justify-start lg:mb-4 lg:gap-2">
                  {currentBanner?.releaseDate && (
                    <>
                      <CaptionSM className="sm:photon-caption-md">
                        {DateTime.fromISO(currentBanner?.releaseDate).year}
                      </CaptionSM>
                      <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                    </>
                  )}
                  <CaptionSM className="sm:photon-caption-md whitespace-nowrap">
                    <Translate i18nKey="special" t={t}>
                      Special
                    </Translate>
                  </CaptionSM>
                  {(currentBanner?.genres?.length ?? 0) > 0 && (
                    <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                  )}
                  {currentBanner?.genres?.map((genre: string | null, index: number) => {
                    return (
                      <div className="flex gap-1 lg:gap-2" key={genre}>
                        <CaptionSM className="sm:photon-caption-md">{genre}</CaptionSM>
                        <When condition={index < (currentBanner?.genres?.length ?? 0) - 1}>
                          <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                        </When>
                      </div>
                    )
                  })}
                </div>
                <div className="md:min-h-auto mb-4 flex min-h-[47px] flex-row flex-wrap items-center justify-center gap-2 md:justify-start lg:mb-8 lg:min-h-7">
                  {currentBanner?.ratings?.[0] && (
                    <ContentRatingBadge
                      contentRating={currentBanner?.ratings?.[0]}
                      className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
                    />
                  )}
                  {currentBanner?.projectSlug && (
                    <HighDefinitionBadge
                      slug={currentBanner?.projectSlug}
                      className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
                    />
                  )}
                  <ClosedCaptionsBadge className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl" />
                  <When condition={currentBanner?.highestScore}>
                    <GuildScore guildScore={currentBanner?.highestScore} hideAnimate />
                  </When>
                </div>
                <div className="relative flex w-full flex-row items-start justify-start gap-4">
                  <LinkButton
                    href={ctaButtonProps?.href ?? ''}
                    variant="white"
                    className="flex max-h-12 w-full items-center gap-[6px] rounded-xl px-6 py-[14px] md:w-fit"
                  >
                    <LabelMD
                      weight="semibold"
                      className="mt-[2.25px] flex flex-row items-center justify-center gap-2"
                      color="core-gray-950"
                    >
                      {ctaButtonProps?.icon}
                      {ctaButtonProps?.ctaText}
                    </LabelMD>
                  </LinkButton>
                  {currentBanner?.id && isLoggedIn && <WatchlistButton titleId={currentBanner.id} />}
                </div>
              </div>
            </PaddedContainer>
          </div>
        </Transition>
      </Fragment>
      <div className="mt-4 flex w-full items-center justify-center md:mt-0">
        <ul className="flex h-full min-w-[98px] flex-row gap-1 md:items-end md:pb-2">
          {heroBanners?.map((item, index) => (
            <li
              key={item.id || index}
              onClick={() => setCurrentSlide(index)}
              className="flex cursor-pointer items-center justify-center p-1"
            >
              <Transition
                show={currentSlide === index}
                className="max-h-[10px] overflow-hidden rounded-full"
                enter="transition ease-in-out duration-850"
                enterFrom="-translate-x-4 scale-x-0 w-2.5 bg-core-gray-500"
                enterTo="translate-x-0 scale-x-1 w-8 bg-white"
              >
                <div
                  className={classNames('bg-white max-h-2.5 h-2.5 w-2.5 rounded-full ', {
                    '!w-8': currentSlide === index,
                  })}
                  key={item.id || index}
                />
              </Transition>
              {currentSlide !== index && (
                <div
                  className={classNames('h-2.5 w-2.5 rounded-full bg-core-gray-500', {
                    'bg-white !w-8': currentSlide === index,
                  })}
                  key={item.id || index}
                />
              )}
            </li>
          ))}
        </ul>
      </div>
      <div className="absolute bottom-2 right-0 hidden min-w-10 lg:mx-12 lg:flex xl:mx-16">
        <div className="relative opacity-0 transition-all duration-300 ease-in-out group-watch-banner-hover:opacity-100">
          <div className="flex flex-row gap-4">
            <Button
              variant="plain"
              onClick={prevSlide}
              className={classNames(
                'pointer-events-auto rounded-full p-2 bg-core-gray-900 opacity-55 hover:opacity-100 transition-all ease-in-out duration-300',
              )}
              aria-label={`Scroll left`}
            >
              <CaretRightIcon size={26} color={'white'} className="rotate-180" />
            </Button>
            <Button
              variant="plain"
              onClick={nextSlide}
              className={classNames(
                'pointer-events-auto rounded-full p-2 bg-core-gray-900 opacity-55 hover:opacity-100 transition-all ease-in-out duration-300',
              )}
              aria-label={`Scroll right`}
            >
              <CaretRightIcon size={26} color={'white'} className="" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
